import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Eye, ArrowUpDown } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { usePermissions } from '../hooks/usePermissions'
import { useDevices, useCreateDevice } from '../hooks/api/useDevices'
import { useTickets } from '../hooks/api/useTickets'
import { ActiveIssuesChart } from '../components/charts/ActiveIssuesChart'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { IssuesEmptyState } from '../components/empty-states/IssuesEmptyState'
import { DeviceInventoryEmptyState } from '../components/empty-states/DeviceInventoryEmptyState'
import { AddDeviceModal } from '../components/modals/AddDeviceModal'
import { Pagination } from '../components/ui/Pagination'
import { DateFilterPopover } from '../components/ui/DateFilterPopover'
import { FilterDropdown, FilterState } from '../components/ui/FilterDropdown'
import { formatDeviceForDisplay } from '../utils/device-utils'

// Helper function to format ticket data for display
const formatTicketForDisplay = (ticket: any) => ({
  id: ticket.id,
  issueId: `S${ticket.id.slice(-2).toUpperCase()}`,
  submittedBy: ticket.created_by_email || 'Unknown',
  description: ticket.title,
  priority: ticket.priority,
  deviceStatus: ticket.device_status || 'Unknown',
  dateSubmitted: new Date(ticket.created_at).toLocaleString(),
  status: ticket.status
})

export const DashboardPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { hasPermission } = usePermissions({ userRole: userRecord?.role || null })
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [ticketsCurrentPage, setTicketsCurrentPage] = useState(1)
  const [ticketsSortBy, setTicketsSortBy] = useState<'priority' | 'date' | null>(null)
  const [ticketsSortOrder, setTicketsSortOrder] = useState<'asc' | 'desc'>('desc')
  const [dateFilter, setDateFilter] = useState('7d') // Default to "Last 7 days"
  const [filters, setFilters] = useState<FilterState>({
    organization: null,
    status: null,
    priority: null
  })
  const itemsPerPage = 5
  const ticketsPerPage = 10
  
  // Get devices from Supabase
  const { data: devices = [], isLoading: devicesLoading } = useDevices(userRecord?.company_id || '')
  const createDeviceMutation = useCreateDevice()
  
  // Get tickets from Supabase
  const { data: tickets = [], isLoading: ticketsLoading } = useTickets(userRecord?.company_id || '')

  const handleAddDevice = () => {
    setShowAddDeviceModal(true)
  }

  const handleCloseModal = () => {
    setShowAddDeviceModal(false)
  }

  const handleDeviceClick = (deviceId: string) => {
    navigate(`/devices/${deviceId}`)
  }

  const handleTicketClick = (ticketId: string) => {
    navigate(`/issues/${ticketId}`)
  }

  // Pagination logic for devices
  const totalPages = Math.ceil(devices.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedDevices = devices.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Filtering logic for tickets
  const filteredTickets = tickets.filter((ticket) => {
    if (filters.status && ticket.status !== filters.status) return false
    if (filters.priority && ticket.priority !== filters.priority) return false
    return true
  })

  // Sorting logic for tickets
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    if (!ticketsSortBy) return 0

    if (ticketsSortBy === 'priority') {
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1, 'urgent': 4 }
      const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 0
      const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 0
      return ticketsSortOrder === 'asc' ? aPriority - bPriority : bPriority - aPriority
    }

    if (ticketsSortBy === 'date') {
      const aDate = new Date(a.created_at).getTime()
      const bDate = new Date(b.created_at).getTime()
      return ticketsSortOrder === 'asc' ? aDate - bDate : bDate - aDate
    }

    return 0
  })

  // Pagination logic for tickets
  const ticketsTotalPages = Math.ceil(sortedTickets.length / ticketsPerPage)
  const ticketsStartIndex = (ticketsCurrentPage - 1) * ticketsPerPage
  const ticketsEndIndex = ticketsStartIndex + ticketsPerPage
  const paginatedTickets = sortedTickets.slice(ticketsStartIndex, ticketsEndIndex)

  const handleTicketsPageChange = (page: number) => {
    setTicketsCurrentPage(page)
  }

  const handleTicketsSort = (column: 'priority' | 'date') => {
    if (ticketsSortBy === column) {
      setTicketsSortOrder(ticketsSortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setTicketsSortBy(column)
      setTicketsSortOrder('desc')
    }
    setTicketsCurrentPage(1) // Reset to first page when sorting
  }

  const handleSubmitDevice = async (deviceData: any) => {
    if (!userRecord?.company_id) {
      console.error('No company ID available')
      return
    }

    try {
      await createDeviceMutation.mutateAsync({
        deviceData: {
          name: deviceData.deviceName,
          os_type: deviceData.operatingSystem,
          device_type: deviceData.deviceType,
          mac_address: deviceData.macAddress,
          user_id: deviceData.deviceOwner ? userRecord.id : undefined
        },
        companyId: userRecord.company_id
      })
      setShowAddDeviceModal(false)
    } catch (error) {
      console.error('Error creating device:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">❌ User not found in database!</p>
            <p className="text-red-500 text-xs mb-2">This account was created before user record creation was implemented.</p>
            <p className="text-red-500 text-xs">Please create a new account to test the full flow.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout 
      userRole={userRecord.role} 
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome! Here's your IT health overview.</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Filter Dropdown */}
            <FilterDropdown
              filters={filters}
              onFilterChange={setFilters}
              storageKey="dashboard-filters"
            />

            {/* Date Filter Popover */}
            <DateFilterPopover
              selectedValue={dateFilter}
              onValueChange={setDateFilter}
            />
            
            {/* Add Device Button */}
            {hasPermission('CREATE_DEVICES') && (
              <button
                onClick={handleAddDevice}
                className="inline-flex items-center px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Device
              </button>
            )}
          </div>
        </div>

        {/* Active Issues Chart - Admin and Manager only */}
        {(userRecord?.role === 'admin' || userRecord?.role === 'manager') && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <ActiveIssuesChart tickets={filteredTickets} />
          </div>
        )}

        {/* Issues Section */}
        {hasPermission('VIEW_TICKETS') && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Issues</h2>
            </div>
            
            {ticketsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-3 border-gray-400 border-t-transparent border-r-transparent"></div>
                <span className="ml-2 text-gray-600">Loading issues...</span>
              </div>
            ) : tickets.length > 0 ? (
              <div className="space-y-4">
                {/* Issues Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Issue ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted by
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              onClick={() => handleTicketsSort('priority')}
                              className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                            >
                              <span>Priority</span>
                              <ArrowUpDown className={`w-3 h-3 ${ticketsSortBy === 'priority' ? 'text-gray-900' : 'text-gray-400'}`} />
                            </button>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Device Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              onClick={() => handleTicketsSort('date')}
                              className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                            >
                              <span>Date Submitted</span>
                              <ArrowUpDown className={`w-3 h-3 ${ticketsSortBy === 'date' ? 'text-gray-900' : 'text-gray-400'}`} />
                            </button>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedTickets.map((ticket) => {
                          const ticketData = formatTicketForDisplay(ticket)
                          
                          return (
                            <tr key={ticket.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {ticketData.issueId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticketData.submittedBy}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {ticketData.description}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  ticketData.priority === 'high' 
                                    ? 'bg-red-100 text-red-800' 
                                    : ticketData.priority === 'medium'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {ticketData.priority === 'high' ? 'High' : 
                                   ticketData.priority === 'medium' ? 'Medium' : 'Low'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  ticketData.deviceStatus === 'online' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    ticketData.deviceStatus === 'online' ? 'bg-green-500' : 'bg-gray-400'
                                  }`}></div>
                                  {ticketData.deviceStatus === 'online' ? 'Online' : 'Offline'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticketData.dateSubmitted}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <button 
                                  onClick={() => handleTicketClick(ticket.id)}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Pagination */}
                {sortedTickets.length > ticketsPerPage && (
                  <Pagination
                    currentPage={ticketsCurrentPage}
                    totalPages={ticketsTotalPages}
                    totalItems={sortedTickets.length}
                    itemsPerPage={ticketsPerPage}
                    onPageChange={handleTicketsPageChange}
                  />
                )}
              </div>
            ) : (
              <IssuesEmptyState />
            )}
          </div>
        )}
        
        {/* Device Inventory Section */}
        {hasPermission('VIEW_DEVICES') && (
          <div className="bg-[#FAFAFA] rounded-2xl border-gray-100 border-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Device Inventory</h2>
              </div>
            
            {devicesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-3 border-gray-400 border-t-transparent border-r-transparent"></div>
                <span className="ml-2 text-gray-600">Loading devices...</span>
              </div>
            ) : devices.length > 0 ? (
              <div className="space-y-4">
                {/* Devices Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Device ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Organization
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tickets
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Active
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedDevices.map((device) => {
                          const deviceData = formatDeviceForDisplay(device)
                          
                          return (
                            <tr key={device.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {deviceData.deviceId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {deviceData.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {deviceData.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {deviceData.organization}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {deviceData.tickets}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {deviceData.lastActive}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <button
                                  onClick={() => handleDeviceClick(device.id)}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
        >
                                  <Eye className="w-4 h-4" />
        </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Pagination */}
                {devices.length > itemsPerPage && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={devices.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            ) : (
              <DeviceInventoryEmptyState onAddDevices={handleAddDevice} />
            )}
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      <AddDeviceModal
        isOpen={showAddDeviceModal}
        onClose={handleCloseModal}
        onAddDevice={handleSubmitDevice}
      />
    </DashboardLayout>
  )
}
